import { useEffect } from 'react';

import { TranslateFunction } from 'react-localize-redux';

import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../../../../core/api/restaurant/restaurant.types';
import { EnqueueSnackbar } from '../../../../core/state/general/general.types';
import { Routes } from '../../../../core/types/route.types';
import { urlUtils } from '../../../../utils/urlUtils';
import { Item, ToCurrency, Total } from '../../../../weborder/BasketControl';
import { checkoutUtils } from '../../utils/checkoutUtils';

export const useBasketRedirects = (
  params: Routes['params'],
  enqueueSnackbar: EnqueueSnackbar,
  translate: TranslateFunction,
  isMenuAndBasketItemsLoaded: boolean,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  category: number,
  currentLang: string,
  basketItems: Item[],
): void => {
  const isBasketEmpty = isMenuAndBasketItemsLoaded && basketItems.length === 0;

  useEffect(() => {
    const redirectToMenuIfRemovedAllItemsInBasket = (): void => {
      enqueueSnackbar(translate('Your_basket_is_empty'), {
        variant: 'warning',
      });

      const restaurantUrl = checkoutUtils.getRestaurantUrl(params);
      urlUtils.navigateToHash(restaurantUrl);
    };

    if (isBasketEmpty && isMenuAndBasketItemsLoaded) {
      redirectToMenuIfRemovedAllItemsInBasket();
    }
  }, [isBasketEmpty, isMenuAndBasketItemsLoaded]);

  useEffect(() => {
    const getCategoryNameTranslation = (): string => {
      switch (category) {
        case 0:
          return 'Restaurant_lowercase';
        case 1:
          return 'Cafe_lowercase';
        case 2:
          return 'Store_lowercase';
        default:
          return 'Restaurant_lowercase';
      }
    };

    const redirectToMenuScreenIfBelowMinimumOrderAmount = (): void => {
      const { MinimumOrder: minimumOrder, Currency: currency } = restaurant;
      const currentBasketTotal = Total(basketItems);

      if (currentBasketTotal < minimumOrder) {
        const categoryNameTranslation = getCategoryNameTranslation();
        const restaurantUrl = checkoutUtils.getRestaurantUrl(params);
        urlUtils.navigateToHash(restaurantUrl);

        const message = translate('Minimum_order_checkout', {
          categoryName: translate(categoryNameTranslation) as string,
          amount: ToCurrency(minimumOrder, currency, currentLang),
        });

        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    };

    if (isMenuAndBasketItemsLoaded && !isBasketEmpty && restaurant) {
      redirectToMenuScreenIfBelowMinimumOrderAmount();
    }
  }, [
    category,
    basketItems.length,
    currentLang,
    isBasketEmpty,
    isMenuAndBasketItemsLoaded,
    params,
    restaurant,
  ]);
};
